import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Container, Form, Table, Card, Row, Col, Modal } from 'react-bootstrap';
import AgreementNameField from '../components/AgreementNameField';
function emptyForm() {
  return {
    name: '',
    depot: '',
    area_name: '',
    transp_stock_no: '',
    rate: '',
    special_instructions: '',
  };
}

function RecordModal({ record, depots, onClose, onSave, saving }) {
  const [formData, setFormData] = useState(emptyForm());
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    setFormData(
      record
        ? {
            name: record.name ?? '',
            depot: record.depot ?? '',
            area_name: record.area_name ?? '',
            transp_stock_no: record.transp_stock_no ?? '',
            rate: record.rate ?? '',
            special_instructions: record.special_instructions ?? '',
          }
        : emptyForm()
    );
    setModalError('');
  }, [record]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSave({
        name: formData.name,
        depot: formData.depot,
        area_name: formData.area_name || null,
        transp_stock_no: formData.transp_stock_no || null,
        rate: formData.rate,
        special_instructions: formData.special_instructions || null,
      });
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to save record');
    }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{record ? 'Edit Transport Rate' : 'Add Transport Rate'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {modalError && <div className="alert alert-danger">{modalError}</div>}
          <AgreementNameField
            inputName="name"
            required
            refreshKey={record?.id ?? 'new'}
            value={formData.name}
            onChange={(name) => setFormData((p) => ({ ...p, name }))}
          />{' '}
          <Form.Group className="mb-3">
            <Form.Label>Depot *</Form.Label>
            <Form.Select
              name="depot"
              value={formData.depot}
              onChange={(e) => setFormData((p) => ({ ...p, depot: e.target.value }))}
              required
            >
              <option value="">Select depot...</option>
              {depots.map((d) => (
                <option key={d.short_code} value={d.short_code}>
                  {d.short_code} — {d.depot_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Area Name</Form.Label>
            <Form.Control
              maxLength={60}
              name="area_name"
              value={formData.area_name}
              onChange={(e) => setFormData((p) => ({ ...p, area_name: e.target.value }))}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Transp Stock No</Form.Label>
            <Form.Control
              maxLength={20}
              name="transp_stock_no"
              value={formData.transp_stock_no}
              onChange={(e) =>
                setFormData((p) => ({ ...p, transp_stock_no: e.target.value.toUpperCase() }))
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Rate *</Form.Label>
            <Form.Control
              type="number"
              name="rate"
              min={0.01}
              step="0.01"
              value={formData.rate}
              onChange={(e) => setFormData((p) => ({ ...p, rate: e.target.value }))}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Special Instructions</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="special_instructions"
              maxLength={2000}
              value={formData.special_instructions}
              onChange={(e) => setFormData((p) => ({ ...p, special_instructions: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function PricingAgreementTransportRates() {
  const [records, setRecords] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const [listRes, depotsRes] = await Promise.all([
        axios.get('/api/pricing-agreement-transport-rates', { params: { page: 1, limit: 10000 } }),
        axios.get('/api/depots/dropdown'),
      ]);
      setRecords(listRes.data.pricingAgreementTransportRates || []);
      setDepots(depotsRes.data || []);
    } catch {
      setError('Failed to fetch records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`/api/pricing-agreement-transport-rates/${editing.id}`, payload);
        setSuccess('Record updated successfully');
      } else {
        await axios.post('/api/pricing-agreement-transport-rates', payload);
        setSuccess('Record added successfully');
      }
      setShowModal(false);
      setEditing(null);
      fetchRecords();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAll = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete ALL transport rate records? This action cannot be undone!'
      )
    ) {
      return;
    }
    if (
      !window.confirm(
        'This will permanently delete all your pricing agreement transport rate records. Are you absolutely sure?'
      )
    ) {
      return;
    }
    try {
      const response = await axios.delete('/api/pricing-agreement-transport-rates');
      setSuccess(
        `All transport rates deleted successfully (${response.data.deletedCount || 0} records)`
      );
      setError('');
      await fetchRecords();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete all transport rates');
    }
  };

  return (
    <Container fluid>
      <Row className="mb-2">
        <Col>
          <h1 className="mb-0">Pricing Agreement Transport Rates</h1>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button
            onClick={() => {
              setEditing(null);
              setShowModal(true);
            }}
          >
            Add Transport Rate
          </Button>
          <Button variant="danger" onClick={handleDeleteAll} disabled={records.length === 0}>
            Delete All
          </Button>
        </Col>
      </Row>
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      <Card>
        <div className="card-body p-0">
          {loading ? (
            <div className="p-4">Loading...</div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Agreement</th>
                  <th>Depot</th>
                  <th>Area</th>
                  <th>Stock No</th>
                  <th>Rate</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td>{row.depot}</td>
                    <td>{row.area_name || '—'}</td>
                    <td>{row.transp_stock_no || '—'}</td>
                    <td>{row.rate}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-1"
                        onClick={() => {
                          setEditing(row);
                          setShowModal(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={async () => {
                          if (!window.confirm('Delete this record?')) return;
                          try {
                            await axios.delete(`/api/pricing-agreement-transport-rates/${row.id}`);
                            fetchRecords();
                          } catch (err) {
                            setError(err.response?.data?.error || 'Failed to delete record');
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>
      {showModal && (
        <RecordModal
          record={editing}
          depots={depots}
          saving={saving}
          onClose={() => !saving && setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </Container>
  );
}

export default PricingAgreementTransportRates;
